/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { useLocation, useParams, useRouter } from '../../../contexts/RouterContext';
import useGetProject from '../../../hooks/api/projects/useGetProject';
import useUpdateProject from '../../../hooks/api/projects/useUpdateProject';
import { useExternalNavigation } from '../../../hooks/useExternalNavigation';
import { isProjectInCurrentOrg, nuc, trimString } from '../../../utils';
import Flex from '../../generic/Flex';
import Tooltip from '../../generic/Tooltip';
import BranchesDropdown from '../overview/BranchesDropdown';
import BadgeModal from '../settingsv2/general/BadgePanel/BadgeModal';
import StatusBadge from '../settingsv2/general/BadgePanel/StatusBadge';
import type { Project, Revision } from '../../../types';
import type { Branch, Tag } from '../overview/BranchesDropdown/types';
import type { RevisionRouteParams } from '../projectDashboardTypes';
import type { BadgeType } from '../settingsv2/general/BadgePanel/types';

import ProjectIcons from './ProjectIcons';
import RevisionAffordances from './RevisionAffordances';
import RevisionDropdown from './RevisionDropdown/Dropdown';

import styles from './DisplayProjectNav.module.less';

type DisplayProjectNavProps = {
  revision?: Revision;
  showBranchDropdown?: boolean;
};

type UseReferenceResult = {
  branches: Branch[];
  tags: Tag[];
};

const useReferences = (project?: Project, selected?: string): UseReferenceResult => useMemo(() => {
  const refs = project?.references ?? [];
  const trackingBranches = new Set(project?.tracking_branches ?? []);
  const hiddenBranches = new Set(project?.hidden_branches ?? []);

  const result: UseReferenceResult = {
    branches: [],
    tags: [],
  };

  return refs.reduce((acc, ref) => {
    if (ref.type === 'branch') {
      acc.branches.push({
        type: 'branch',
        name: ref.name,
        revisionLocator: ref.revision_id,
        isSelected: ref.name === selected,
        isTracked: trackingBranches.has(ref.name),
        isArchived: hiddenBranches.has(ref.name),
        isDefault: ref.name === project?.default_branch,
      });
    } else if (ref.type === 'tag') {
      acc.tags.push({
        type: 'tag',
        name: ref.name,
        revisionLocator: ref.revision_id,
        isSelected: ref.name === selected,
      });
    }
    return acc;
  }, result);
}, [project, selected]);

const TITLE_LENGTH_THRESHOLD = 50;

const DisplayProjectNav: VoidFunctionComponent<DisplayProjectNavProps> = ({
  revision,
  showBranchDropdown,
}) => {
  // Get URL params and router from context
  const params = useParams<RevisionRouteParams>();
  const router = useRouter();
  const location = useLocation();

  // Fetch project data
  const { data: project, isLoading, error } = useGetProject(
    params.project_id,
    {
      ref_type: 'branch',
      ref: params.reference,
      ...(location?.query?.ref ? { badge_ref: location.query.ref } : {}),
    },
  );

  // Mutations for project updates
  const { mutateAsync: updateProject } = useUpdateProject();

  // Local UI state
  const [shouldShowBadgeModal, setShouldShowBadgeModal] = useState(false);
  const [badgeModalTab, setBadgeModalTab] = useState<BadgeType>('licenseShield');

  // Computed values
  const reference = params.reference || project?.default_branch || 'master';
  const revisionId =
    params.revision_id ||
    (project?.last_analyzed_revision ? Locator.parse(project.last_analyzed_revision).revision : '');

  // Navigation handlers

  const handleNavigateProjectHome = useExternalNavigation(project?.url);
  const handleNavigateRevisionLink = useExternalNavigation(revision?.link);

  // Branch operation handlers
  const handleTrackDefaultBranch = useCallback(async () => {
    if (!project?.locator) {
      return;
    }

    const defaultBranch = project.default_branch ?? 'master';
    await updateProject({
      projectLocator: project.locator,
      data: {
        tracking_branches: [defaultBranch],
      },
    });
  }, [project?.locator, project?.default_branch, updateProject]);

  const handleBranchClick = useCallback(
    (ref: Branch | Tag) => {
      const projectLocator = project?.locator;
      if (!projectLocator) {
        return;
      }

      const revisionLocator = Locator.parse(ref.revisionLocator).revision;
      if (!revisionLocator) {
        return;
      }

      const branchURL = `/projects/${nuc(projectLocator)}/refs/${ref.type}/${nuc(ref.name)}/${nuc(revisionLocator)}`;
      router.push(branchURL);
    },
    [project?.locator, router],
  );

  const handleArchiveBranch = useCallback(
    async (branch: Branch) => {
      if (!project?.locator) {
        return;
      }

      let tracking_branches = [...(project.tracking_branches ?? [])];
      const hidden_branches = [...(project.hidden_branches ?? []), branch.name];

      // If the branch is tracked, untrack it (by removing from tracking_branches).
      if (branch.isTracked) {
        tracking_branches = tracking_branches.filter((name) => name !== branch.name);
      }

      // Apply branch updates to the project.
      await updateProject({
        projectLocator: project.locator,
        data: {
          tracking_branches,
          hidden_branches,
        },
      });
    },
    [project?.locator, project?.tracking_branches, project?.hidden_branches, updateProject],
  );

  const handleUnarchiveBranch = useCallback(
    async (branch: Branch) => {
      if (!project?.locator) {
        return;
      }

      const hidden_branches = (project.hidden_branches ?? []).filter((name) => name !== branch.name);

      // Apply branch updates to the project.
      await updateProject({
        projectLocator: project.locator,
        data: {
          hidden_branches,
        },
      });
    },
    [project?.locator, project?.hidden_branches, updateProject],
  );

  const handleTrackBranch = useCallback(
    async (branch: Branch) => {
      if (!project?.locator) {
        return;
      }

      const tracking_branches = [...(project.tracking_branches ?? []), branch.name];

      // Apply branch updates to the project.
      await updateProject({
        projectLocator: project.locator,
        data: {
          tracking_branches,
        },
      });
    },
    [project?.locator, project?.tracking_branches, updateProject],
  );

  const handleUntrackBranch = useCallback(
    async (branch: Branch) => {
      if (!project?.locator) {
        return;
      }

      const tracking_branches = (project.tracking_branches ?? []).filter((name) => name !== branch.name);

      // Apply branch updates to the project.
      await updateProject({
        projectLocator: project.locator,
        data: {
          tracking_branches,
        },
      });
    },
    [project?.locator, project?.tracking_branches, updateProject],
  );

  // Render computed values
  const referenceType = params.reference_type || 'branch';
  const baseUrl = `/projects/${nuc(params.project_id)}/`;
  const revisionUrl = `${baseUrl}refs/${referenceType}/${nuc(reference)}/${nuc(revisionId || '')}`;

  const { branches, tags } = useReferences(project, params.reference);

  const helmetTitle = project?.title || 'Loading ...';

  const isProjectNotFound = (error && 'status' in error && error.status === 404);
  const isCurrentOrgProject = isProjectInCurrentOrg(project?.organizationId ?? 0);

  const projectTitle = useMemo(() => {
    const linkTo = {
      pathname: isCurrentOrgProject ? revisionUrl : `${revisionUrl}/preview`,
    };

    const titleLink = (
      <Link className="project-title-link" to={linkTo}>
        {project?.title}
      </Link>
    );

    if ((project?.title?.length ?? 0) > TITLE_LENGTH_THRESHOLD) {
      return (
        <Tooltip tooltip={project?.title} id={project?.locator || ''}>
          {titleLink}
        </Tooltip>
      );
    }

    return titleLink;
  }, [isCurrentOrgProject, revisionUrl, project?.title, project?.locator]);

  const revisionsDropdown = useMemo(() => {
    if (params.reference && revision) {
      return (
        <div className="nav-project-description">
          <RevisionDropdown
            project={{ data: project, status: { loading: isLoading, error } }}
            reference={params.reference}
            referenceType={params.reference_type}
            selectedRevision={revision}
            onSelectRevision={(selectedRevision) => {
              if (params.reference_type && params.reference) {
                router.push(
                  `/projects/${nuc(params.project_id)}/refs/${nuc(params.reference_type)}/${nuc(params.reference)}/${nuc(
                    selectedRevision.loc.revision,
                  )}`,
                );
              }
            }}
          />
        </div>
      );
    }

    if (!isLoading && project?.description) {
      return trimString(project.description, 175);
    }

    return 'No description available';
  }, [error, isLoading, params.project_id, params.reference, params.reference_type, project, revision, router]);

  if (isLoading) {
    return (<i className="subtitle">loading...</i>);
  }

  if (isProjectNotFound) {
    return (<i className="subtitle">Not Found</i>);
  }

  return (
    <div>
      <Helmet title={`${helmetTitle} - Projects`} />
      <div className="project-title-section">
        <h1 className="nav-project-description">
          <span className="animated fadeInLeft">
            <span>
              {projectTitle}

              {project && (
                <ProjectIcons
                  project={{
                    type: project.type,
                    locator: project.locator,
                    public: project.public,
                    vcs_host: project.vcs_host,
                    url: project.url,
                  }}
                  revision={revision}
                  onNavigateProjectHome={handleNavigateProjectHome}
                  onNavigateRevisionLink={handleNavigateRevisionLink}
                />
              )}
              <span className="pull-right">
                <Tooltip id="status-badge" tooltip="Click for badge embed code" placement="bottom">
                  <div className="inline-block">
                    <Flex direction="column" align="flex-start" gap="8px">
                      <StatusBadge
                        locator={params.project_id}
                        badgeType="licenseShield"
                        onClick={() => {
                          setShouldShowBadgeModal(true);
                          setBadgeModalTab('licenseShield');
                        }}
                      />
                      {project?.securityIssueScanningEnabled && (
                        <StatusBadge
                          locator={params.project_id}
                          badgeType="securityShield"
                          onClick={() => {
                            setShouldShowBadgeModal(true);
                            setBadgeModalTab('securityShield');
                          }}
                        />
                      )}
                    </Flex>
                  </div>
                </Tooltip>
                <BadgeModal
                  locator={params.project_id}
                  isSecurityEnabled={Boolean(project?.securityIssueScanningEnabled)}
                  shouldShow={shouldShowBadgeModal}
                  defaultTab={badgeModalTab}
                  onHide={() => setShouldShowBadgeModal(false)}
                />
              </span>
            </span>
          </span>
        </h1>

        <Flex justify="flex-start" gap="10px">
          <h1 className="nav-project-description">
            {params.reference && (showBranchDropdown || revision) && (
              <BranchesDropdown
                branches={branches}
                tags={tags}
                defaultBranch={project?.default_branch ?? 'master'}
                onTrackDefaultBranch={handleTrackDefaultBranch}
                onClick={handleBranchClick}
                onArchiveBranch={handleArchiveBranch}
                onUnarchiveBranch={handleUnarchiveBranch}
                onTrackBranch={handleTrackBranch}
                onUntrackBranch={handleUntrackBranch}
              />
            )}
          </h1>
          <p className={`animated fadeInDown fast ${styles.revisionDropDown}`}>
            {revisionsDropdown}
          </p>
          <RevisionAffordances locator={revision?.locator || ''} />
        </Flex>
      </div>
    </div>
  );
};

export default DisplayProjectNav;
